import { Divider } from "@blueprintjs/core";

import { Box, Flex } from "@/components/layout/flexbox";
import { LIGHT_STEEL_GRAY, MIDTEXT_COLOR } from "@/css/constants";
import { RoleplaySessionTranscriptFragment } from "@/graphql";
import { ensureNonNullArray } from "@/utils/array";
import { NonNullableArrayItem } from "@/utils/types";

type TranscriptCriterion = NonNullableArrayItem<
  RoleplaySessionTranscriptFragment["evaluatedCriterion"]
>;

type RoleplaySessionTranscriptCriterionItem = NonNullableArrayItem<
  TranscriptCriterion["items"]
>;

type RoleplaySessionTranscriptFeedbackExample = NonNullableArrayItem<
  RoleplaySessionTranscriptCriterionItem["feedbacks"]
>;

// Component for rendering the header of the evaluation card
function CriterionHeader({ name }: { name: string }) {
  return (
    <Box
      p={3}
      fontWeight={500}
      fontSize={18}
      mb={2}
      className="round-corners"
      style={{
        backgroundColor: LIGHT_STEEL_GRAY,
        borderRadius: "6px 6px 0px 0px",
      }}
    >
      {name}
    </Box>
  );
}

// Component for rendering a criterion item
function CriterionItem({
  item,
}: {
  item: RoleplaySessionTranscriptCriterionItem;
}) {
  const feedbacks = ensureNonNullArray(item.feedbacks);
  if (feedbacks.length === 0) return null;

  return (
    <Box key={item.id} p={2}>
      <Box color={MIDTEXT_COLOR} fontWeight={700} fontSize={14}>
        {item.name}
      </Box>
      {feedbacks.map((feedbackExample, index) => (
        <FeedbackExampleItem
          key={feedbackExample.id}
          feedbackExample={feedbackExample}
          isFirst={index === 0}
        />
      ))}
    </Box>
  );
}

// Component for rendering feedback examples
function FeedbackExampleItem({
  feedbackExample,
  isFirst,
}: {
  feedbackExample: RoleplaySessionTranscriptFeedbackExample;
  isFirst: boolean;
}) {
  const { feedback } = feedbackExample;

  return (
    <Box key={feedbackExample.id}>
      {!isFirst && (
        <Divider
          style={{
            margin: "16px 0px 0px 0px",
          }}
        />
      )}
      <Flex
        flexDirection="column"
        style={{
          gap: "8px",
        }}
      >
        <Box mt={2} color={MIDTEXT_COLOR} fontWeight={400} fontSize={14}>
          {feedback}
        </Box>
      </Flex>
    </Box>
  );
}

interface DetailFeedbackEvaluationCardProps {
  criterion: TranscriptCriterion;
}

/**
 * DetailFeedbackEvaluationCard - Displays evaluation criteria with feedback
 *
 * @param {DetailFeedbackEvaluationCardProps} props - Component props
 * @returns Rendered component
 *
 */
export function DetailFeedbackEvaluationCard({
  criterion,
}: DetailFeedbackEvaluationCardProps) {
  const items = ensureNonNullArray(criterion.items);
  return (
    <Box key={criterion.id} my={4} className="bas round-corners">
      <CriterionHeader name={criterion.name ?? ""} />
      {items.map((criterionItem) => (
        <CriterionItem key={criterionItem.id} item={criterionItem} />
      ))}
    </Box>
  );
}
